import { useEffect, useState } from 'react';
import {
  Package,
  ClipboardList,
  Settings,
  Bell,
  Shield,
  Banknote,
  ShieldCheck,
  Users,
  TrendingUp,
  Building2,
  Home,
  CheckSquare,
  Briefcase,
  BarChart3,
  Plus,
  CalendarDays,
  FileText,
  Eye,
  FileSearch,
  Menu,
  Upload,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  BellIcon,
  HomeIcon,
  PortalSidebar,
  SidebarGroup,
  SidebarItem,
  readPortalSidebarCollapsed,
  writePortalSidebarCollapsed,
} from './portal/PortalSidebar';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface SidebarProps {
  currentPage: string;
  onPageChange: (page: string) => void;
  desktopCollapsed?: boolean;
  onDesktopCollapsedChange?: (collapsed: boolean) => void;
}

const FUNNEL_APP_URL = process.env.REACT_APP_FUNNEL_APP_URL || 'https://funnel.surbhi.net';
const INSURANCE_APP_URL =
  process.env.REACT_APP_INSURANCE_APP_URL || 'https://insurance.surbhi.net';
const EMD_APP_URL = process.env.REACT_APP_EMD_APP_URL || 'https://apps.surbhi.net/emd';
const ACTIVITY_APP_URL =
  process.env.REACT_APP_ACTIVITY_APP_URL || 'https://activity.surbhi.net';
const LETTER_APP_URL = process.env.REACT_APP_LETTER_APP_URL || 'https://letter.surbhi.net';

export function Sidebar({ currentPage, onPageChange, desktopCollapsed, onDesktopCollapsedChange }: SidebarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopCollapsedInternal, setIsDesktopCollapsed] = useState<boolean>(readPortalSidebarCollapsed);
  const isDesktopCollapsed = desktopCollapsed ?? isDesktopCollapsedInternal;
  const handleDesktopCollapsedChange = (next: boolean) => {
    setIsDesktopCollapsed(next);
    writePortalSidebarCollapsed(next);
    onDesktopCollapsedChange?.(next);
  };
  const clearLetterNavigationParams = (params: URLSearchParams) => {
    params.delete('letterId');
    params.delete('openLetterId');
    params.delete('templateId');
    params.delete('useTemplateId');
    return params;
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [currentPage]);

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    logout();
  };

  const permissions = user?.permissions;
  const isVikas = user?.email?.toLowerCase() === 'vikas@surbhi.net';
  const normalizedEmail = String(user?.email || '')
    .trim()
    .toLowerCase();
  const canAccessUploadPdf = ['vikas@surbhu.net', 'krati@surbhi.net', 'vikas@surbhi.net'].includes(
    normalizedEmail
  );

  // Define portal-specific menus
  const getMenuItemsForPage = (page: string) => {
    const canSeeTaskTrackerAllMenus = user?.permissions?.is_admin === true || isVikas;

    const taskTrackerMenus = canSeeTaskTrackerAllMenus
      ? [
          {
            id: 'tasktracker-dashboard',
            label: 'Dashboard',
            icon: BarChart3,
            requiresPermission: 'can_view_tasktracker' as const,
          },
          {
            id: 'tasktracker-review-emp',
            label: 'Review Emp',
            icon: Users,
            requiresPermission: 'can_view_tasktracker' as const,
          },
          {
            id: 'tasktracker',
            label: 'Calendar',
            icon: CheckSquare,
            requiresPermission: 'can_view_tasktracker' as const,
          },
          { id: 'notifications', label: 'Notifications', icon: Bell, requiresPermission: null },
          { id: 'tasktracker-admin', label: 'Team Task', icon: Shield, requiresPermission: null },
          {
            id: 'tasktracker-organisation',
            label: 'Organisation',
            icon: Briefcase,
            requiresPermission: null,
          },
          {
            id: 'tasktracker-upload-calendar',
            label: 'Upload Calendar',
            icon: CalendarDays,
            requiresPermission: null,
          },
        ]
      : [
          {
            id: 'tasktracker',
            label: 'Calendar',
            icon: CheckSquare,
            requiresPermission: 'can_view_tasktracker' as const,
          },
          { id: 'notifications', label: 'Notifications', icon: Bell, requiresPermission: null },
        ];

    const canAccessLetterAdmin = permissions?.is_admin === true || isVikas;
    const lettersMenus = [
      {
        id: 'letters-dashboard',
        label: 'Dashboard',
        icon: BarChart3,
        requiresPermission: 'can_view_letters',
      },
      {
        id: 'letters-create',
        label: 'Create Letter',
        icon: Plus,
        requiresPermission: 'can_view_letters',
      },
      {
        id: 'letters-view-templates',
        label: 'View Templates',
        icon: FileSearch,
        requiresPermission: 'can_view_letters',
      },
      {
        id: 'letters-view',
        label: 'View Letters',
        icon: Eye,
        requiresPermission: 'can_view_letters',
      },
      {
        id: 'letters-drafts',
        label: 'View Drafts',
        icon: FileSearch,
        requiresPermission: 'can_view_letters',
      },
      ...(canAccessLetterAdmin
        ? [
            {
              id: 'letters-create-template',
              label: 'Create Template',
              icon: Plus,
              requiresPermission: 'can_view_letters',
            },
          ]
        : []),
      {
        id: 'letters-pending-approval',
        label: 'Approval',
        icon: CheckSquare,
        requiresPermission: 'can_view_letters',
      },
      ...(canAccessLetterAdmin
        ? [
            {
              id: 'letters-admin',
              label: 'Admin',
              icon: Shield,
              requiresPermission: 'can_view_letters',
            },
          ]
        : []),
    ];

    // Portal-specific menus - application pages come first
    const portalMenus: Record<
      string,
      Array<{ id: string; label: string; icon: any; requiresPermission: string | null }>
    > = {
      // Task Portal
      tasks: [
        {
          id: 'tasks',
          label: 'User Dashboard',
          icon: ClipboardList,
          requiresPermission: 'can_view_tasks',
        },
        ...(user?.permissions?.is_admin
          ? [
              {
                id: 'tasks-by-users',
                label: 'Team Dashboard',
                icon: Users,
                requiresPermission: 'can_view_tasks',
              },
            ]
          : []),
      ],
      'tasks-by-users': [
        {
          id: 'tasks',
          label: 'User Dashboard',
          icon: ClipboardList,
          requiresPermission: 'can_view_tasks',
        },
        ...(user?.permissions?.is_admin
          ? [
              {
                id: 'tasks-by-users',
                label: 'Team Dashboard',
                icon: Users,
                requiresPermission: 'can_view_tasks',
              },
            ]
          : []),
      ],
      // Product Portal
      products: [
        {
          id: 'products-dashboard',
          label: 'Dashboard',
          icon: BarChart3,
          requiresPermission: 'can_view_products',
        },
        {
          id: 'products-add-item',
          label: 'Add Item',
          icon: Plus,
          requiresPermission: 'can_view_products',
        },
      ],
      // EMD Portal
      emd: [{ id: 'emd', label: 'EMD / ePBG', icon: Banknote, requiresPermission: 'can_view_emd' }],
      // SEPL Portal
      sepl: [{ id: 'sepl', label: 'SEPL Funnel', icon: TrendingUp, requiresPermission: null }],
      // Insurance Portal
      insurance: [
        {
          id: 'insurance',
          label: 'Insurance Portal',
          icon: ShieldCheck,
          requiresPermission: 'can_view_insurance',
        },
      ],
      // Billing Portal
      'billing-portal': [
        {
          id: 'billing-portal',
          label: 'Billing Portal',
          icon: Banknote,
          requiresPermission: 'can_view_billing_portal',
        },
      ],
      // HR Recruitement Portal
      'hr-recruitement': [
        {
          id: 'hr-recruitement',
          label: 'Enroll Applicant',
          icon: Building2,
          requiresPermission: 'can_view_hr_recruitment',
        },
        {
          id: 'organisation-positions',
          label: 'Organisation Positions',
          icon: Briefcase,
          requiresPermission: 'can_view_hr_recruitment',
        },
      ],
      'organisation-positions': [
        {
          id: 'hr-recruitement',
          label: 'Enroll Applicant',
          icon: Building2,
          requiresPermission: 'can_view_hr_recruitment',
        },
        {
          id: 'organisation-positions',
          label: 'Organisation Positions',
          icon: Briefcase,
          requiresPermission: 'can_view_hr_recruitment',
        },
      ],
      // Task Tracker
      tasktracker: taskTrackerMenus,
      'tasktracker-dashboard': taskTrackerMenus,
      'tasktracker-review-emp': taskTrackerMenus,
      'tasktracker-tasks': taskTrackerMenus,
      'tasktracker-meetings': taskTrackerMenus,
      'tasktracker-admin': taskTrackerMenus,
      'tasktracker-organisation': taskTrackerMenus,
      'tasktracker-upload-calendar': taskTrackerMenus,
      'tasktracker-notifications': taskTrackerMenus,
      letters: lettersMenus,
      'letters-dashboard': lettersMenus,
      'letters-create': lettersMenus,
      'letters-create-template': lettersMenus,
      'letters-view-templates': lettersMenus,
      'letters-view': lettersMenus,
      'letters-create-fresh': lettersMenus,
      'letters-upload-pdf': lettersMenus,
      'letters-drafts': lettersMenus,
      'letters-pending-approval': lettersMenus,
      'letters-admin': lettersMenus,
      'letters-role-assignment': lettersMenus,
      'letters-add-signature': lettersMenus,
      // Dashboard pages - no portal items
      dashboard: [],
      'auth-logs': [],
      settings: [],
      admin: [],
      'admin-org': [],
      notifications: [],
    };

    // Get portal-specific items or empty array
    const portalItems = portalMenus[page] || [];

    // Core navigation items (Home) - always at the bottom
    const coreNavItems = [{ id: 'dashboard', label: 'Home', icon: Home, requiresPermission: null }];

    // Admin items based on page and user
    const isTaskTracker =
      page === 'tasktracker' ||
      page === 'tasktracker-dashboard' ||
      page === 'tasktracker-tasks' ||
      page === 'tasktracker-meetings' ||
      page === 'tasktracker-admin' ||
      page === 'tasktracker-organisation' ||
      page === 'tasktracker-upload-calendar' ||
      page === 'tasktracker-notifications';

    const adminItems = [];

    // Never show admin items on tasktracker pages - admin is only in tasktracker sidebar itself
    if (!isTaskTracker) {
      // Admin items removed from menu
    }

    return [...portalItems, ...coreNavItems, ...adminItems];
  };

  // Get menu items for current page
  const allMenuItems = getMenuItemsForPage(currentPage);

  const permittedItems = allMenuItems.filter((item) => {
    if (!item.requiresPermission) return true;
    // If permissions are not yet loaded, be permissive so core menus appear
    if (!permissions || typeof permissions !== 'object') return true;

    if (item.id === 'admin' && isVikas) return true;

    // Handle both boolean and integer (MySQL 0/1) values
    const permissionValue = permissions[item.requiresPermission as keyof typeof permissions];
    return permissionValue === true || permissionValue === 1;
  });

  // Show only permitted items for current portal
  const menuItems = permittedItems;

  // Determine if we're on a tasktracker page
  const isTaskTracker =
    currentPage === 'tasktracker' ||
    currentPage === 'tasktracker-dashboard' ||
    currentPage === 'tasktracker-review-emp' ||
    currentPage === 'tasktracker-tasks' ||
    currentPage === 'tasktracker-meetings' ||
    currentPage === 'tasktracker-admin' ||
    currentPage === 'tasktracker-organisation' ||
    currentPage === 'tasktracker-upload-calendar' ||
    currentPage === 'tasktracker-notifications';
  const isLetters =
    currentPage === 'letters-dashboard' ||
    currentPage === 'letters-create' ||
    currentPage === 'letters-create-template' ||
    currentPage === 'letters-create-fresh' ||
    currentPage === 'letters-upload-pdf' ||
    currentPage === 'letters-view' ||
    currentPage === 'letters-view-templates' ||
    currentPage === 'letters-drafts' ||
    currentPage === 'letters-pending-approval' ||
    currentPage === 'letters-admin' ||
    currentPage === 'letters-role-assignment' ||
    currentPage === 'letters-add-signature';
  const [lettersMenuOpen, setLettersMenuOpen] = useState(false);
  const [templatesMenuOpen, setTemplatesMenuOpen] = useState(false);

  const isLettersCreateActive =
    currentPage === 'letters-create' ||
    currentPage === 'letters-create-fresh' ||
    currentPage === 'letters-upload-pdf';
  const isLettersViewActive = currentPage === 'letters-view';
  const isTemplatesCreateActive = currentPage === 'letters-create-template';
  const isTemplatesViewActive = currentPage === 'letters-view-templates';
  const brand = (() => {
    if (isTaskTracker)
      return { icon: CheckSquare, eyebrow: 'Task Tracking & Project Management', title: 'Activity Management Portal' };
    if (currentPage === 'products')
      return { icon: Package, eyebrow: 'Product Reminders', title: 'Product Portal' };
    if (currentPage === 'tasks' || currentPage === 'tasks-by-users')
      return { icon: ClipboardList, eyebrow: 'Productivity Hub', title: 'Daily Task Tracker' };
    if (currentPage === 'emd')
      return { icon: Banknote, eyebrow: 'EMD / ePBG Management', title: 'EMD Portal' };
    if (currentPage === 'insurance')
      return { icon: ShieldCheck, eyebrow: 'Policy Management', title: 'Insurance Portal' };
    if (currentPage === 'billing-portal')
      return { icon: Banknote, eyebrow: 'Financial Management', title: 'Billing Portal' };
    if (currentPage === 'hr-recruitement' || currentPage === 'organisation-positions')
      return { icon: Building2, eyebrow: 'Hiring & Positions', title: 'Enroll Applicant' };
    if (currentPage === 'sepl')
      return { icon: TrendingUp, eyebrow: 'Sales Pipeline', title: 'SEPL Funnel' };
    if (isLetters) return { icon: FileText, eyebrow: 'Letter Portal', title: 'Document Management' };
    if (currentPage === 'admin' || currentPage === 'admin-org')
      return { icon: Shield, eyebrow: 'System Management', title: 'Admin Portal' };
    if (currentPage === 'settings')
      return { icon: Settings, eyebrow: 'Preferences', title: 'Settings' };
    return { icon: Menu, eyebrow: 'Menu', title: 'SEPL Portal' };
  })();
  useEffect(() => {
    if (isLettersCreateActive || isLettersViewActive) {
      setLettersMenuOpen(true);
    }
    if (isTemplatesCreateActive || isTemplatesViewActive) {
      setTemplatesMenuOpen(true);
    }
  }, [isLettersCreateActive, isLettersViewActive, isTemplatesCreateActive, isTemplatesViewActive]);

  const navigateToSidebarPage = (id: string) => {
    if (id === 'sepl') {
      window.location.href = FUNNEL_APP_URL;
      setIsMobileMenuOpen(false);
      return;
    }

    if (id === 'emd') {
      window.location.href = EMD_APP_URL;
      setIsMobileMenuOpen(false);
      return;
    }

    if (id === 'insurance') {
      window.location.href = INSURANCE_APP_URL;
      setIsMobileMenuOpen(false);
      return;
    }

    if (id === 'tasktracker' || id.startsWith('tasktracker-')) {
      window.location.href = `${ACTIVITY_APP_URL.replace(/\/$/, '')}/dashboard`;
      setIsMobileMenuOpen(false);
      return;
    }

    if (id.startsWith('letters-') && window.location.hostname !== 'letter.surbhi.net') {
      window.location.href = `${LETTER_APP_URL}?page=${id}`;
      setIsMobileMenuOpen(false);
      return;
    }

    const params = new URLSearchParams(searchParams);

    if (id === 'products-dashboard') {
      params.set('page', 'products');
      params.delete('subpage');
    } else if (id === 'products-add-item') {
      params.set('page', 'products');
      params.set('subpage', 'add-item');
    } else if (id === 'notifications' && isTaskTracker) {
      params.set('page', 'tasktracker-notifications');
      params.delete('subpage');
    } else {
      params.set('page', id);
      params.delete('subpage');
    }

    if (String(id).startsWith('letters-')) {
      clearLetterNavigationParams(params);
    }

    setSearchParams(params);
    setIsMobileMenuOpen(false);
  };

  // Separate portal items from core/admin items
  // For TaskTracker, keep notifications in portalItems. For other pages, move it to coreItems
  const lettersAdminIds = ['letters-admin', 'letters-role-assignment', 'letters-add-signature'];
  const portalItems = menuItems.filter((item) => {
    if (isTaskTracker) {
      return !['dashboard', 'settings', 'admin'].includes(item.id);
    } else if (isLetters) {
      // Show all letter items except admin items (shown below separator)
      return !['dashboard', 'notifications', 'settings', 'admin', ...lettersAdminIds].includes(
        item.id
      );
    } else {
      return !['dashboard', 'notifications', 'settings', 'admin'].includes(item.id);
    }
  });
  const lettersAdminItems = isLetters
    ? menuItems.filter((item) => lettersAdminIds.includes(item.id))
    : [];
  const coreItems = menuItems.filter((item) => {
    if (isTaskTracker) {
      return item.id === 'dashboard';
    } else if (isLetters) {
      return item.id === 'dashboard';
    } else {
      return item.id === 'dashboard';
    }
  });
  const adminItems = menuItems.filter((item) => ['settings', 'admin'].includes(item.id));

  const isItemActive = (id: string) =>
    currentPage === id ||
    (id === 'notifications' && currentPage === 'tasktracker-notifications') ||
    (id === 'products-dashboard' && currentPage === 'products' && !searchParams.get('subpage')) ||
    (id === 'products-add-item' && currentPage === 'products' && searchParams.get('subpage') === 'add-item');

  const goToCentralHome = () => {
    setIsMobileMenuOpen(false);
    window.location.href = 'https://apps.surbhi.net/dashboard';
  };

  const openLettersAdminPage = (id: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', id);
    params.delete('subpage');
    if (String(id).startsWith('letters-')) {
      clearLetterNavigationParams(params);
    }
    setSearchParams(params);
    setIsMobileMenuOpen(false);
  };

  return (
    <PortalSidebar
      brand={brand}
      expanded={!isDesktopCollapsed}
      onExpandedChange={(next) => handleDesktopCollapsedChange(!next)}
      mobileOpen={isMobileMenuOpen}
      onMobileOpenChange={setIsMobileMenuOpen}
      user={{ name: user?.name, email: user?.email }}
      onLogout={handleLogout}
    >
      {isLetters ? (
        <>
          <SidebarItem
            icon={BarChart3}
            label="Dashboard"
            active={currentPage === 'letters-dashboard'}
            onClick={() => navigateToSidebarPage('letters-dashboard')}
          />
          <SidebarGroup
            icon={FileText}
            label="Letters"
            active={isLettersCreateActive || isLettersViewActive}
            open={lettersMenuOpen}
            onOpenChange={setLettersMenuOpen}
          >
            <SidebarItem
              icon={Plus}
              label="Create Letter"
              active={isLettersCreateActive}
              onClick={() => navigateToSidebarPage('letters-create')}
            />
            <SidebarItem
              icon={Eye}
              label="View Letters"
              active={isLettersViewActive}
              onClick={() => navigateToSidebarPage('letters-view')}
            />
            {canAccessUploadPdf && (
              <SidebarItem
                icon={Upload}
                label="Upload Module"
                active={currentPage === 'letters-upload-pdf'}
                onClick={() => navigateToSidebarPage('letters-upload-pdf')}
              />
            )}
          </SidebarGroup>
          <SidebarItem
            icon={FileSearch}
            label="Drafts"
            active={currentPage === 'letters-drafts'}
            onClick={() => navigateToSidebarPage('letters-drafts')}
          />
          <SidebarGroup
            icon={FileSearch}
            label="Templates"
            active={isTemplatesCreateActive || isTemplatesViewActive}
            open={templatesMenuOpen}
            onOpenChange={setTemplatesMenuOpen}
          >
            <SidebarItem
              icon={Plus}
              label="Create Template"
              active={isTemplatesCreateActive}
              onClick={() => navigateToSidebarPage('letters-create-template')}
            />
            <SidebarItem
              icon={Eye}
              label="View Templates"
              active={isTemplatesViewActive}
              onClick={() => navigateToSidebarPage('letters-view-templates')}
            />
          </SidebarGroup>
          {portalItems.some((item) => item.id === 'letters-pending-approval') && (
            <SidebarItem
              icon={CheckSquare}
              label="Approval"
              active={currentPage === 'letters-pending-approval'}
              onClick={() => navigateToSidebarPage('letters-pending-approval')}
            />
          )}
        </>
      ) : (
        portalItems.map(({ id, label, icon }) => (
          <SidebarItem
            key={id}
            icon={id === 'notifications' ? BellIcon : icon}
            label={label}
            active={isItemActive(id)}
            onClick={() => navigateToSidebarPage(id)}
          />
        ))
      )}

      {lettersAdminItems.map(({ id, label, icon }) => (
        <SidebarItem
          key={id}
          icon={icon}
          label={label}
          active={currentPage === id}
          onClick={() => openLettersAdminPage(id)}
        />
      ))}

      {adminItems.map(({ id, label, icon }) => (
        <SidebarItem
          key={id}
          icon={icon}
          label={label}
          active={currentPage === id}
          onClick={() => onPageChange(id)}
        />
      ))}

      {/* Home — always last */}
      {coreItems.map(({ id, label }) => (
        <SidebarItem key={id} icon={HomeIcon} label={label} onClick={goToCentralHome} />
      ))}
    </PortalSidebar>
  );
}
